(() => {
  if (window.FourPawAnalytics) return;

  const allowed = (values) => {
    const allowlist = new Set(values);
    return (value) => (allowlist.has(value) ? value : undefined);
  };
  const boolean = (value) => (typeof value === "boolean" ? value : undefined);
  const integer = (value) => {
    const number = Number(value);
    return Number.isInteger(number) && number >= 0 && number <= 1000 ? number : undefined;
  };

  const size = allowed(["XS", "S", "M", "L", "XL"]);
  const color = allowed([
    "aqua",
    "beige",
    "black",
    "gray",
    "orange",
    "pink",
    "red",
    "sky_blue",
  ]);
  const packageKind = allowed(["harness_only", "harness_bungee", "harness_collar", "full_set"]);
  const accessoryKind = allowed(["leash", "collar", "safebelt"]);
  const schemas = {
    "fourpaw:product_viewed": {},
    "fourpaw:gallery_changed": {
      position: integer,
      media_count: integer,
      media_type: allowed(["image", "video", "model", "personalization", "unknown"]),
      source: allowed(["thumbnail", "dot", "arrow", "swipe"]),
      is_personalization_preview: boolean,
    },
    "fourpaw:size_finder_opened": {},
    "fourpaw:size_finder_calculated": {
      method: allowed(["weight", "breed"]),
      outcome: allowed(["recommended", "guidance", "needs_input"]),
      recommended_size: size,
      unit: allowed(["lb", "kg"]),
    },
    "fourpaw:size_recommendation_applied": {
      recommended_size: size,
      method: allowed(["weight", "breed"]),
    },
    "fourpaw:variant_option_selected": {
      option_kind: allowed(["color", "size"]),
      option_value: allowed([
        "XS",
        "S",
        "M",
        "L",
        "XL",
        "aqua",
        "beige",
        "black",
        "gray",
        "orange",
        "pink",
        "red",
        "sky_blue",
      ]),
      source: allowed(["customer"]),
    },
    "fourpaw:package_selected": { package: packageKind },
    "fourpaw:personalization_opened": {},
    "fourpaw:personalization_state_changed": {
      name_present: boolean,
      phone_present: boolean,
    },
    "fourpaw:add_to_cart_attempted": {
      package: packageKind,
      name_present: boolean,
      phone_present: boolean,
      trigger_source: allowed(["main", "sticky"]),
      attempt_number: integer,
    },
    "fourpaw:add_to_cart_accepted": { attempt_number: integer, line_count: integer },
    "fourpaw:cart_sync_failed": { attempt_number: integer },
    "fourpaw:add_to_cart_succeeded": {
      package: packageKind,
      name_present: boolean,
      phone_present: boolean,
      attempt_number: integer,
      line_count: integer,
    },
    "fourpaw:add_to_cart_failed": {
      failure_category: allowed(["validation", "availability", "network", "timeout", "unknown"]),
      attempt_number: integer,
    },
    "fourpaw:checkout_clicked": {
      source: allowed(["drawer", "cart_page"]),
    },
    "fourpaw:cart_upsell_viewed": { accessory: accessoryKind },
    "fourpaw:cart_upsell_attempted": { accessory: accessoryKind },
    "fourpaw:cart_upsell_added": { accessory: accessoryKind },
    "fourpaw:cart_upsell_failed": { accessory: accessoryKind },
  };

  const clarityEventNames = Object.freeze({
    "fourpaw:product_viewed": "FP_ProductViewed",
    "fourpaw:gallery_changed": "FP_GalleryInteraction",
    "fourpaw:size_finder_opened": "FP_SizeFinderOpened",
    "fourpaw:size_finder_calculated": "FP_SizeFinderCalculated",
    "fourpaw:size_recommendation_applied": "FP_SizeRecommendationApplied",
    "fourpaw:variant_option_selected": "FP_VariantSelected",
    "fourpaw:package_selected": "FP_PackageSelected",
    "fourpaw:personalization_opened": "FP_PersonalizationOpened",
    "fourpaw:personalization_state_changed": "FP_PersonalizationChanged",
    "fourpaw:add_to_cart_attempted": "FP_AddToCartAttempted",
    "fourpaw:add_to_cart_accepted": "FP_AddToCartAccepted",
    "fourpaw:cart_sync_failed": "FP_CartSyncFailed",
    "fourpaw:add_to_cart_succeeded": "FP_AddToCartSucceeded",
    "fourpaw:add_to_cart_failed": "FP_AddToCartFailed",
    "fourpaw:checkout_clicked": "FP_CheckoutClicked",
    "fourpaw:cart_upsell_viewed": "FP_CartUpsellViewed",
    "fourpaw:cart_upsell_attempted": "FP_CartUpsellAttempted",
    "fourpaw:cart_upsell_added": "FP_CartUpsellAdded",
    "fourpaw:cart_upsell_failed": "FP_CartUpsellFailed",
  });

  const clarityQueue = [];
  const clarityRetryLimit = 80;
  const clarityRetryDelay = 250;
  let clarityRetryCount = 0;
  let clarityRetryTimer = null;
  let clarityContextTagged = false;

  const sanitize = (eventName, data = {}) => {
    const schema = schemas[eventName];
    if (!schema) return null;
    const payload = {};
    Object.entries(schema).forEach(([key, normalize]) => {
      const value = normalize(data[key]);
      if (value !== undefined) payload[key] = value;
    });
    return payload;
  };

  const clarityContext = () => {
    const country = String(window.Shopify?.country || "unknown").toUpperCase();
    const mode = window.Shopify?.designMode
      ? "editor"
      : window.Shopify?.theme?.role === "unpublished" || new URLSearchParams(window.location.search).has("preview_theme_id")
        ? "preview"
        : "storefront";
    const template = document.querySelector(".shop-product-section fourpaw-package-selector")
      ? "safewalk"
      : document.getElementById("main-cart-footer")
        ? "cart"
        : "other";
    return { country, mode, template };
  };

  const tagClarityContext = () => {
    if (clarityContextTagged || typeof window.clarity !== "function") return;
    const context = clarityContext();
    window.clarity("set", "fourpaw_market", context.country);
    window.clarity("set", "fourpaw_theme_mode", context.mode);
    window.clarity("set", "fourpaw_template", context.template);
    clarityContextTagged = true;
  };

  const scheduleClarityFlush = () => {
    if (clarityRetryTimer || clarityRetryCount >= clarityRetryLimit) return;
    clarityRetryTimer = window.setTimeout(() => {
      clarityRetryTimer = null;
      clarityRetryCount += 1;
      flushClarityQueue();
    }, clarityRetryDelay);
  };

  const flushClarityQueue = () => {
    if (typeof window.clarity !== "function") {
      if (clarityRetryCount >= clarityRetryLimit) clarityQueue.length = 0;
      else scheduleClarityFlush();
      return false;
    }

    try {
      tagClarityContext();
      while (clarityQueue.length) {
        window.clarity("event", clarityQueue[0]);
        clarityQueue.shift();
      }
      clarityRetryCount = 0;
      return true;
    } catch (_error) {
      if (clarityRetryCount >= clarityRetryLimit) clarityQueue.length = 0;
      else scheduleClarityFlush();
      return false;
    }
  };

  const publishClarity = (eventName) => {
    const clarityEventName = clarityEventNames[eventName];
    if (!clarityEventName) return false;
    clarityQueue.push(clarityEventName);
    flushClarityQueue();
    return true;
  };

  const publish = (eventName, data = {}) => {
    const payload = sanitize(eventName, data);
    if (!payload) return false;

    try {
      window.Shopify?.analytics?.publish?.(eventName, payload);
    } catch (_error) {
    }

    publishClarity(eventName);

    document.dispatchEvent(
      new CustomEvent("fourpaw:analytics", {
        detail: { eventName, payload },
      }),
    );
    return true;
  };

  const sectionFor = (element) => element?.closest?.(".shop-product-section") || null;
  const purchaseFlowFor = (element) => {
    const section = sectionFor(element);
    return section?.querySelector("fourpaw-package-selector") ? section : null;
  };
  const selectedPackage = (section) => {
    const includesBungee = Boolean(
      section?.querySelector('[data-package-addon="bungee"] [data-package-input]:checked'),
    );
    const includesCollar = Boolean(
      section?.querySelector('[data-package-addon="collar"] [data-package-input]:checked'),
    );
    if (includesBungee && includesCollar) return "full_set";
    if (includesBungee) return "harness_bungee";
    if (includesCollar) return "harness_collar";
    return "harness_only";
  };
  const personalizationState = (section) => ({
    name_present: Boolean(
      section?.querySelector('[data-fourpaw-personalization-role="dog_name"] input')?.value.trim(),
    ),
    phone_present: Boolean(
      section?.querySelector('[data-fourpaw-personalization-role="phone"] input')?.value.trim(),
    ),
  });

  let attemptNumber = 0;
  let pendingAttempt = null;
  let triggerSource = "main";
  const personalizationStates = new WeakMap();

  const tagClarityTargets = (root = document) => {
    const targets = [
      [".shop-thumbnail, .shop-dot, .shop-indicator-dot, .shop-desktop-prev-arrow, .shop-desktop-next-arrow, .shop-mobile-prev-arrow, .shop-mobile-next-arrow", "gallery"],
      ["[data-size-chart-popup]", "size-finder-open"],
      ["[data-size-calculate], [data-size-breed-calculate]", "size-finder-calculate"],
      ["[data-size-apply]", "size-finder-apply"],
      [".simple-variant-picker__option.option--color .simple-variant-picker__color-swatch", "color-select"],
      [".simple-variant-picker__option.option--size .simple-variant-picker__value.size-value", "size-select"],
      ["[data-package-card]", "package-select"],
      [".fourpaw-personalization-disclosure__toggle", "personalization-open"],
      ["product-form [type='submit']", "add-to-cart"],
      ["[data-package-sticky-button]", "sticky-add-to-cart"],
      ["cart-drawer [name='checkout'], cart-drawer a[href*='/checkout']", "checkout"],
      ["cart-drawer [data-fourpaw-cart-upsell-button]", "cart-upsell-add"],
      ["main [name='checkout'], main a[href*='/checkout']", "checkout"],
    ];
    targets.forEach(([selector, label]) => {
      root.querySelectorAll?.(selector).forEach((element) => {
        element.dataset.fourpawClarityEvent = label;
      });
    });
  };

  const trackSizeCalculation = (button) => {
    const finder = button.closest("fourpaw-size-finder");
    if (!finder) return;
    const method = button.matches("[data-size-breed-calculate]") ? "breed" : "weight";
    const recommendedSize = size(String(finder.recommendedSize || "").toUpperCase());
    const hasAnyInput =
      method === "breed"
        ? Boolean(finder.breedSelect?.value)
        : Boolean(finder.weightInput?.value || finder.chestInput?.value);
    publish("fourpaw:size_finder_calculated", {
      method,
      outcome: recommendedSize && finder.canApplyRecommendation ? "recommended" : hasAnyInput ? "guidance" : "needs_input",
      recommended_size: recommendedSize,
      unit: finder.unit,
    });
  };

  const trackVariantSelection = (input, event) => {
    if (!event.isTrusted || !purchaseFlowFor(input)) return;
    const option = input.closest(".simple-variant-picker__option");
    const optionKind = option?.classList.contains("option--size")
      ? "size"
      : option?.classList.contains("option--color")
        ? "color"
        : null;
    if (!optionKind) return;

    const rawValue = String(input.value || "").trim();
    const normalizedValue =
      optionKind === "size"
        ? size(rawValue.toUpperCase())
        : color(rawValue.toLowerCase().replace(/[\s-]+/g, "_"));
    publish("fourpaw:variant_option_selected", {
      option_kind: optionKind,
      option_value: normalizedValue,
      source: "customer",
    });
  };

  document.addEventListener("product-gallery:interaction", (event) => {
    publish("fourpaw:gallery_changed", event.detail || {});
  });

  document.addEventListener("click", (event) => {
    const target = event.target.closest?.("button, a, label");
    if (!target) return;

    if (target.matches("[data-size-chart-popup]")) {
      publish("fourpaw:size_finder_opened");
    }

    if (target.matches("[data-size-calculate], [data-size-breed-calculate]")) {
      window.setTimeout(() => trackSizeCalculation(target), 0);
    }

    if (target.matches("[data-size-apply]")) {
      const finder = target.closest("fourpaw-size-finder");
      publish("fourpaw:size_recommendation_applied", {
        recommended_size: size(String(finder?.recommendedSize || "").toUpperCase()),
        method: finder?.querySelector('[data-size-method][aria-selected="true"]')?.dataset.sizeMethod,
      });
    }

    if (target.matches(".fourpaw-personalization-disclosure__toggle")) {
      window.setTimeout(() => {
        if (target.getAttribute("aria-expanded") === "true") {
          publish("fourpaw:personalization_opened");
        }
      }, 0);
    }

    if (target.matches("[data-package-sticky-button]")) {
      triggerSource = "sticky";
      window.setTimeout(() => {
        triggerSource = "main";
      }, 500);
    }

    const checkout = target.closest("[name='checkout'], a[href*='/checkout']");
    if (checkout) {
      publish("fourpaw:checkout_clicked", {
        source: checkout.closest("cart-drawer") ? "drawer" : "cart_page",
      });
    }
  });

  document.addEventListener("keydown", (event) => {
    if (event.key !== "Enter") return;
    const input = event.target.closest?.("[data-size-weight], [data-size-chest], [data-size-breed-search]");
    if (!input) return;
    const finder = input.closest("fourpaw-size-finder");
    const button = input.matches("[data-size-breed-search]")
      ? finder?.querySelector("[data-size-breed-calculate]")
      : finder?.querySelector("[data-size-calculate]");
    if (button) window.setTimeout(() => trackSizeCalculation(button), 0);
  });

  document.addEventListener("change", (event) => {
    const input = event.target;
    if (input.matches?.(".simple-variant-picker__radio, .simple-variant-picker__select")) {
      trackVariantSelection(input, event);
    }
  });

  document.addEventListener("fourpaw:package-change", (event) => {
    publish("fourpaw:package_selected", { package: event.detail?.package });
  });

  document.addEventListener("fourpaw:cart-upsell-viewed", (event) => {
    publish("fourpaw:cart_upsell_viewed", event.detail || {});
  });

  document.addEventListener("fourpaw:cart-upsell-attempted", (event) => {
    publish("fourpaw:cart_upsell_attempted", event.detail || {});
  });

  document.addEventListener("fourpaw:cart-upsell-added", (event) => {
    publish("fourpaw:cart_upsell_added", event.detail || {});
  });

  document.addEventListener("fourpaw:cart-upsell-failed", (event) => {
    publish("fourpaw:cart_upsell_failed", event.detail || {});
  });

  document.addEventListener("focusout", (event) => {
    const wrapper = event.target.closest?.("[data-fourpaw-personalization-role]");
    if (!wrapper) return;
    const section = purchaseFlowFor(wrapper);
    if (!section) return;
    const state = personalizationState(section);
    const stateKey = `${state.name_present}:${state.phone_present}`;
    if (personalizationStates.get(section) === stateKey) return;
    personalizationStates.set(section, stateKey);
    publish("fourpaw:personalization_state_changed", state);
  });

  document.addEventListener(
    "submit",
    (event) => {
      const form = event.target;
      const productForm = form.closest?.("product-form");
      const section = purchaseFlowFor(productForm);
      const submitButton = productForm?.querySelector("[type='submit']");
      if (!section || submitButton?.getAttribute("aria-disabled") === "true") return;
      const state = personalizationState(section);
      pendingAttempt = {
        attempt_number: ++attemptNumber,
        package: selectedPackage(section),
        ...state,
      };
      publish("fourpaw:add_to_cart_attempted", {
        ...pendingAttempt,
        trigger_source: triggerSource,
      });
      triggerSource = "main";
    },
    true,
  );

  document.addEventListener("fourpaw:product-form:add-to-cart-accepted", (event) => {
    if (!pendingAttempt || !purchaseFlowFor(event.target)) return;
    publish("fourpaw:add_to_cart_accepted", {
      attempt_number: pendingAttempt.attempt_number, line_count: event.detail?.lineCount,
    });
  });
  document.addEventListener("fourpaw:product-form:cart-sync-failed", (event) => {
    if (!pendingAttempt || !purchaseFlowFor(event.target)) return;
    publish("fourpaw:cart_sync_failed", { attempt_number: pendingAttempt.attempt_number });
    pendingAttempt = null;
  });

  document.addEventListener("fourpaw:product-form:add-to-cart-succeeded", (event) => {
    if (!pendingAttempt || !purchaseFlowFor(event.target)) return;
    publish("fourpaw:add_to_cart_succeeded", {
      ...pendingAttempt,
      line_count: event.detail?.lineCount,
    });
    pendingAttempt = null;
  });

  document.addEventListener("fourpaw:product-form:add-to-cart-failed", (event) => {
    if (!pendingAttempt || !purchaseFlowFor(event.target)) return;
    publish("fourpaw:add_to_cart_failed", {
      failure_category: event.detail?.failureCategory || "unknown",
      attempt_number: pendingAttempt.attempt_number,
    });
    pendingAttempt = null;
  });

  document.addEventListener("shopify:section:load", (event) => tagClarityTargets(event.target));
  tagClarityTargets();

  if (document.querySelector(".shop-product-section fourpaw-package-selector")) {
    publish("fourpaw:product_viewed");
  }

  window.FourPawAnalytics = Object.freeze({
    publish,
    publishClarity,
    sanitize,
    getDiagnostics: () => ({
      clarity_available: typeof window.clarity === "function",
      clarity_queue_length: clarityQueue.length,
      clarity_retry_count: clarityRetryCount,
      clarity_context_tagged: clarityContextTagged,
    }),
  });
})();
